import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import onrampService from '../services/onrampService.js';
import supportedService from '../services/supportedService.js';
import walletPurchaseService from '../services/walletPurchaseService.js';
import './BuyCrypto.css';

// Country flag emoji mapping for common currencies
const CURRENCY_FLAGS = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', INR: '🇮🇳', AUD: '🇦🇺',
  CAD: '🇨🇦', JPY: '🇯🇵', CHF: '🇨🇭', SGD: '🇸🇬', HKD: '🇭🇰',
  NZD: '🇳🇿', SEK: '🇸🇪', NOK: '🇳🇴', DKK: '🇩🇰', PLN: '🇵🇱',
  BRL: '🇧🇷', MXN: '🇲🇽', ZAR: '🇿🇦', AED: '🇦🇪', THB: '🇹🇭',
  PHP: '🇵🇭', MYR: '🇲🇾', IDR: '🇮🇩', VND: '🇻🇳', KRW: '🇰🇷',
  TRY: '🇹🇷', RUB: '🇷🇺', CZK: '🇨🇿', HUF: '🇭🇺', ILS: '🇮🇱',
};

// Crypto icon colors (ensure sufficient contrast with white text)
const CRYPTO_COLORS = {
  ETH: '#627eea', BTC: '#e8860c', USDC: '#2775ca', USDT: '#26a17b',
  MATIC: '#8247e5', BNB: '#c99400', SOL: '#9945ff', AVAX: '#e84142',
  ARB: '#28a0f0', OP: '#ff0420', BASE: '#0052ff', TRX: '#eb0029',
  tBNB: '#c99400', DAI: '#f5ac37',
};

// Network icon colors (ensure sufficient contrast with white text)
const NETWORK_COLORS = {
  ethereum: '#627eea',
  arbitrum: '#28a0f0',
  polygon: '#8247e5',
  optimism: '#ff0420',
  base: '#0052ff',
  bsc: '#c99400',
  bsc_testnet: '#c99400',
  avalanche: '#e84142',
  solana: '#9945ff',
  tron: '#eb0029',
  bitcoin: '#e8860c',
};

// Provider logo URLs (fallback, will be overridden by API data)
const PROVIDER_LOGOS = {
  transak: 'https://cdn.onramper.com/icons/onramps/transak-colored.svg',
  banxa: 'https://cdn.onramper.com/icons/onramps/banxa-colored.svg',
  'onramp-money': 'https://onramp.money/images/onramp-logo.svg',
  onmeta: 'https://onmeta.in/logo.svg',
  guardarian: 'https://guardarian.com/main-logo-dark.png',
  onramper: 'https://cdn.onramper.com/icons/onramps/onramper-colored.svg',
};

// Currency-specific minimum and maximum amounts (approximate USD equivalents)
// MIN ~ $30 USD equivalent, MAX ~ $50,000 USD equivalent
// These are conservative frontend guards; providers enforce their own exact limits
const CURRENCY_LIMITS = {
  USD: { min: 30, max: 50000 },
  EUR: { min: 28, max: 46000 },
  GBP: { min: 24, max: 40000 },
  INR: { min: 2500, max: 4200000 },
  CAD: { min: 42, max: 69000 },
  AUD: { min: 46, max: 77000 },
  JPY: { min: 4500, max: 7500000 },
  CHF: { min: 27, max: 45000 },
  SGD: { min: 40, max: 67000 },
  HKD: { min: 235, max: 390000 },
  NZD: { min: 50, max: 83000 },
  SEK: { min: 320, max: 530000 },
  NOK: { min: 330, max: 550000 },
  DKK: { min: 210, max: 345000 },
  PLN: { min: 120, max: 200000 },
  BRL: { min: 150, max: 250000 },
  MXN: { min: 520, max: 860000 },
  ZAR: { min: 550, max: 910000 },
  AED: { min: 110, max: 184000 },
  THB: { min: 1050, max: 1750000 },
  PHP: { min: 1700, max: 2800000 },
  MYR: { min: 140, max: 230000 },
  IDR: { min: 480000, max: 790000000 },
  VND: { min: 750000, max: 1250000000 },
  KRW: { min: 40000, max: 66000000 },
  TRY: { min: 970, max: 1600000 },
  CZK: { min: 700, max: 1150000 },
  HUF: { min: 11000, max: 18500000 },
  ILS: { min: 110, max: 183000 },
};
const DEFAULT_CURRENCY_LIMITS = { min: 30, max: 50000 };

// Currency symbols for display in error messages
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', AUD: 'A$',
  CAD: 'C$', JPY: '¥', CHF: 'CHF ', SGD: 'S$', HKD: 'HK$',
  AED: 'د.إ', THB: '฿', PHP: '₱', MYR: 'RM ', KRW: '₩',
  BRL: 'R$', MXN: 'MX$', IDR: 'Rp ', VND: '₫', NZD: 'NZ$',
  SEK: 'kr ', NOK: 'kr ', DKK: 'kr ', PLN: 'zł', ZAR: 'R ',
  TRY: '₺', CZK: 'Kč ', HUF: 'Ft ', ILS: '₪',
};

/**
 * BuyCrypto Component
 *
 * Main widget for buying cryptocurrency via on-ramp providers.
 *
 * Props:
 * @param {Object} config - Configuration options
 * @param {string} config.defaultAmount - Default fiat amount
 * @param {string} config.defaultFiat - Default fiat currency (USD, EUR, etc.)
 * @param {string} config.defaultCrypto - Default cryptocurrency (USDT, ETH, etc.)
 * @param {string} config.defaultNetwork - Default blockchain network
 * @param {string} config.walletAddress - Pre-filled wallet address
 * @param {boolean} config.isLocked - If true, amount/currency/crypto/network are read-only (for payment links)
 * @param {Object} config.lockedSession - Session data when isLocked is true
 * @param {Function} config.onCheckout - Custom checkout handler (for payment link flow)
 * @param {Object} bridge - PostMessage bridge for iframe communication
 * @param {boolean} isEmbedded - Whether widget is embedded in iframe
 */
const BuyCrypto = ({ config = {}, bridge = null, isEmbedded = false }) => {
  const [loading, setLoading] = useState(false);

  // Locked mode flag - when true, amount/currency/crypto/network cannot be changed
  // This is used when the widget is opened from a secure payment link
  const isLocked = config.isLocked || false;

  // Per-field lock flags (for exchange/wallet iframe integration via URL params)
  // Wallet is ALWAYS disabled in the widget — must be passed via URL (?wallet=0x...).
  // Without a wallet, the field stays empty + disabled and the Buy button shows
  // "Connect Wallet to Continue" prompting the integrator to provide it.
  const isWalletLocked = true;
  const isAmountLocked = isLocked || !!config.lockAmount;
  const isFiatLocked = isLocked || !!config.lockFiat;
  const isCryptoLocked = isLocked || !!config.lockCrypto;

  // Form state - use config defaults if provided
  // When isLocked=true, these values come from the payment session and cannot be modified
  const [fiatAmount, setFiatAmount] = useState(config.defaultAmount || '');
  const [fiatCurrency, setFiatCurrency] = useState(config.defaultFiat || 'EUR');
  const [cryptoCurrency, setCryptoCurrency] = useState(config.defaultCrypto || 'USDT');
  const [network, setNetwork] = useState(config.defaultNetwork || 'arbitrum');
  const [paymentMethod, setPaymentMethod] = useState('');
  const paymentMethodManuallySelected = useRef(false);
  const [walletAddress, setWalletAddress] = useState(config.walletAddress || '');
  const [walletError, setWalletError] = useState(null);

  // Amount validation
  const [amountError, setAmountError] = useState('');

  // Options loaded from API
  const [fiatCurrencies, setFiatCurrencies] = useState([]);
  const [cryptoCurrencies, setCryptoCurrencies] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Provider icons from API (keyed by provider name)
  const [providerIcons, setProviderIcons] = useState({});

  // Helper to get crypto icon from loaded data
  const getCryptoIcon = useCallback((code, networkName) => {
    const crypto = cryptoCurrencies.find(
      c => c.code === code && (!networkName || c.network === networkName)
    );
    return crypto?.icon || null;
  }, [cryptoCurrencies]);

  // Helper to get network icon from loaded data
  const getNetworkIcon = useCallback((networkName) => {
    const net = networks.find(n => n.name === networkName);
    return net?.icon || null;
  }, [networks]);

  // Provider quotes
  const [providerQuotes, setProviderQuotes] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState(null);
  const [providerErrors, setProviderErrors] = useState({});

  // Quote staleness tracking
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isStale, setIsStale] = useState(false);

  // AbortController ref for cleanup
  const abortControllerRef = useRef(null);

  // Force refetch counter (incremented on amount input to handle same-value re-entry)
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Provider selection modal
  const [showProvidersModal, setShowProvidersModal] = useState(false);

  // Dropdown states
  const [showFiatDropdown, setShowFiatDropdown] = useState(false);
  const [showCryptoDropdown, setShowCryptoDropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [showFeeBreakdown, setShowFeeBreakdown] = useState(null);
  const [cryptoSearch, setCryptoSearch] = useState('');
  // Two-step crypto dropdown: 'tokens' (step 1) or 'network' (step 2 — pick chain for multi-network token)
  const [cryptoDropdownStep, setCryptoDropdownStep] = useState('tokens');
  // Token group pending network selection (set when user picks a multi-network token in step 1)
  const [pendingTokenGroup, setPendingTokenGroup] = useState(null);

  // Refs for click outside
  const fiatDropdownRef = useRef(null);
  const cryptoDropdownRef = useRef(null);
  const paymentDropdownRef = useRef(null);

  // Screen reader announcement
  const [announcement, setAnnouncement] = useState('');

  // Wallet mode purchase tracking
  const isWalletMode = config.mode === 'wallet';
  const [activePurchaseId, setActivePurchaseId] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState(null);

  // Disclaimer consent
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Normal mode: awaiting payment detection (popup → success page → BroadcastChannel)
  const [awaitingPayment, setAwaitingPayment] = useState({
    active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null,
  });
  const popupWindowRef = useRef(null);
  const [paymentComplete, setPaymentComplete] = useState(null);

  // Helper to safely extract array from API response
  const extractArray = (value, ...keys) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') {
      for (const key of keys) {
        if (Array.isArray(value[key])) return value[key];
      }
    }
    return [];
  };

  // Fallback data
  const fallbackFiats = [
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  ];

  // Fallback crypto+network combinations (each is a selectable item)
  const fallbackCryptoNetworks = [
    { code: 'USDT', name: 'Tether', network: 'arbitrum', networkName: 'Arbitrum' },
    { code: 'USDT', name: 'Tether', network: 'ethereum', networkName: 'Ethereum' },
    { code: 'USDT', name: 'Tether', network: 'polygon', networkName: 'Polygon' },
    { code: 'USDT', name: 'Tether', network: 'solana', networkName: 'Solana' },
    { code: 'USDC', name: 'USD Coin', network: 'arbitrum', networkName: 'Arbitrum' },
    { code: 'USDC', name: 'USD Coin', network: 'ethereum', networkName: 'Ethereum' },
    { code: 'USDC', name: 'USD Coin', network: 'polygon', networkName: 'Polygon' },
    { code: 'USDC', name: 'USD Coin', network: 'base', networkName: 'Base' },
    { code: 'USDC', name: 'USD Coin', network: 'solana', networkName: 'Solana' },
    { code: 'ETH', name: 'Ethereum', network: 'ethereum', networkName: 'Ethereum' },
    { code: 'ETH', name: 'Ethereum', network: 'arbitrum', networkName: 'Arbitrum' },
    { code: 'ETH', name: 'Ethereum', network: 'optimism', networkName: 'Optimism' },
    { code: 'ETH', name: 'Ethereum', network: 'base', networkName: 'Base' },
    { code: 'BTC', name: 'Bitcoin', network: 'bitcoin', networkName: 'Bitcoin' },
    { code: 'SOL', name: 'Solana', network: 'solana', networkName: 'Solana' },
    { code: 'MATIC', name: 'Polygon', network: 'polygon', networkName: 'Polygon' },
  ];

  // Legacy fallback for backward compatibility
  const fallbackCryptos = fallbackCryptoNetworks;

  const fallbackNetworks = [
    { name: 'ethereum', displayName: 'Ethereum' },
    { name: 'arbitrum', displayName: 'Arbitrum' },
    { name: 'polygon', displayName: 'Polygon' },
    { name: 'optimism', displayName: 'Optimism' },
    { name: 'base', displayName: 'Base' },
  ];

  // Use unified payment method IDs that work with both Transak and Banxa
  const fallbackMethods = [
    { id: 'card', name: 'Credit/Debit Card', recommended: true },
    { id: 'bank_transfer', name: 'Bank Transfer' },
    { id: 'apple_pay', name: 'Apple Pay' },
    { id: 'google_pay', name: 'Google Pay' },
  ];

  // Group tokens by symbol for two-step selection (Token → Network)
  // Each group: { symbol, name, icon, networks: [{ network, networkName, ...tokenData }] }
  const tokenGroups = useMemo(() => {
    const groups = new Map();
    const networkLocked = config.networkFilter; // URL ?network= param locks to one network

    cryptoCurrencies.forEach(c => {
      const code = c.symbol || c.code || c.ticker || '';
      if (!code) return;
      // When network is locked via URL, only include tokens on that network
      if (networkLocked && (c.network || '').toLowerCase() !== networkLocked.toLowerCase()) return;

      if (!groups.has(code)) {
        groups.set(code, {
          symbol: code,
          name: c.displayName || c.name || code,
          icon: c.icon || null,
          networks: [],
        });
      }
      const group = groups.get(code);
      // Avoid duplicate networks for the same token (case-insensitive comparison)
      const netKey = (c.network || '').toLowerCase();
      if (!group.networks.find(n => (n.network || '').toLowerCase() === netKey)) {
        group.networks.push({
          network: c.network || '',
          networkName: c.networkName || (c.network ? c.network.charAt(0).toUpperCase() + c.network.slice(1) : ''),
          icon: c.icon,
          ...c,
        });
      }
    });

    // Sort: popular tokens first (USDT, USDC, ETH, BTC...), then alphabetically
    const POPULAR = ['USDT', 'USDC', 'ETH', 'BTC', 'BNB', 'SOL', 'MATIC', 'TRX', 'DAI', 'POL'];
    return Array.from(groups.values()).sort((a, b) => {
      const ai = POPULAR.indexOf(a.symbol);
      const bi = POPULAR.indexOf(b.symbol);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.symbol.localeCompare(b.symbol);
    });
  }, [cryptoCurrencies, config.networkFilter]);

  // Helper: get currency limits (min/max) for the current fiat currency
  const getCurrencyLimits = useCallback((currencyCode) => {
    return CURRENCY_LIMITS[currencyCode] || DEFAULT_CURRENCY_LIMITS;
  }, []);

  // Helper: get currency symbol for display
  const getCurrencySymbol = useCallback((currencyCode) => {
    const fiatObj = fiatCurrencies.find(f => (f.code || f) === currencyCode);
    if (fiatObj?.symbol) return fiatObj.symbol;
    return CURRENCY_SYMBOLS[currencyCode] || currencyCode + ' ';
  }, [fiatCurrencies]);

  // Amount validation function (dynamic per currency)
  const validateAmount = useCallback((value, currencyCode = fiatCurrency) => {
    const num = parseFloat(value);
    if (!value || String(value).trim() === '') {
      setAmountError('');
      return false;
    }
    if (isNaN(num) || num <= 0) {
      setAmountError('Please enter a valid amount');
      return false;
    }

    const limits = getCurrencyLimits(currencyCode);
    const symbol = getCurrencySymbol(currencyCode);

    if (num < limits.min) {
      setAmountError(`Minimum amount is ${symbol}${limits.min.toLocaleString()}`);
      return false;
    }
    if (num > limits.max) {
      setAmountError(`Maximum amount is ${symbol}${limits.max.toLocaleString()}`);
      return false;
    }
    setAmountError('');
    return true;
  }, [fiatCurrency, getCurrencyLimits, getCurrencySymbol]);

  // Re-validate amount when currency changes (minimum/maximum thresholds differ per currency)
  useEffect(() => {
    if (fiatAmount) {
      validateAmount(fiatAmount);
    }
  }, [fiatCurrency, fiatAmount, validateAmount]);

  // Quote staleness checker
  useEffect(() => {
    if (!lastFetchTime) return;
    const interval = setInterval(() => {
      const staleAfterMs = 30000; // 30 seconds
      setIsStale(Date.now() - lastFetchTime > staleAfterMs);
    }, 1000);
    return () => clearInterval(interval);
  }, [lastFetchTime]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fiatDropdownRef.current && !fiatDropdownRef.current.contains(e.target)) {
        setShowFiatDropdown(false);
      }
      if (cryptoDropdownRef.current && !cryptoDropdownRef.current.contains(e.target)) {
        setShowCryptoDropdown(false);
      }
      if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(e.target)) {
        setShowPaymentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch options from API (using new supportedService with fallback to old onrampService)
  useEffect(() => {
    const fetchOptions = async () => {
      setOptionsLoading(true);
      try {
        // Try new supported API first, fallback to old onrampService
        const [
          fiatResult,
          cryptoResult,
          networksResult,
          paymentResult,
          onrampsResult
        ] = await Promise.allSettled([
          supportedService.getFiatCurrencies(),
          supportedService.getTokens(),
          supportedService.getNetworks(),
          supportedService.getPaymentMethods(config.defaultFiat || 'EUR'),
          supportedService.getAllOnramps()
        ]);

        // Process fiat currencies from new API
        let fiats = fallbackFiats;
        if (fiatResult.status === 'fulfilled' && fiatResult.value) {
          const fiatData = Array.isArray(fiatResult.value) ? fiatResult.value : [];
          if (fiatData.length > 0) {
            fiats = fiatData.map(f => ({
              code: f.code,
              name: f.name,
              symbol: f.symbol,
              icon: f.icon
            }));
          }
        }
        setFiatCurrencies(fiats);

        // Process tokens from new API - already in crypto+network format
        let cryptos = fallbackCryptos;
        if (cryptoResult.status === 'fulfilled' && cryptoResult.value) {
          const tokenData = Array.isArray(cryptoResult.value) ? cryptoResult.value : [];
          if (tokenData.length > 0) {
            cryptos = tokenData.map(token => ({
              code: token.symbol,
              name: token.name,
              network: token.network,
              networkName: token.networkDisplayName || token.network,
              icon: token.icon,
              isStablecoin: token.isStablecoin
            }));
          }
        }
        console.log('Loaded cryptoCurrencies:', cryptos.slice(0, 5));
        setCryptoCurrencies(cryptos);

        // Process networks from new API
        let nets = fallbackNetworks;
        if (networksResult.status === 'fulfilled' && networksResult.value) {
          const netData = Array.isArray(networksResult.value) ? networksResult.value : [];
          if (netData.length > 0) {
            nets = netData.map(n => ({
              name: n.name,
              displayName: n.displayName,
              chainId: n.chainId,
              icon: n.icon
            }));
          }
        }
        setNetworks(nets);

        // Process payment methods from new API
        let methods = fallbackMethods;
        if (paymentResult.status === 'fulfilled' && paymentResult.value) {
          const methodData = Array.isArray(paymentResult.value) ? paymentResult.value : [];
          if (methodData.length > 0) {
            methods = methodData.map((m, idx) => ({
              id: m.paymentTypeId || m.id,
              code: m.paymentTypeId || m.id,
              name: m.name,
              icon: m.icon,
              recommended: idx === 0 // First is highest priority
            }));
          }
        }
        setPaymentMethods(methods);

        // Set default payment method (prefer config param, then recommended, then first)
        if (config.paymentMethod) {
          const configMethod = methods.find(m => (m.id || m.code) === config.paymentMethod);
          if (configMethod) {
            setPaymentMethod(configMethod.id || configMethod.code);
          } else {
            setPaymentMethod(config.paymentMethod);
          }
        } else {
          const defaultMethod = methods.find(m => m.recommended) || methods[0];
          if (defaultMethod) {
            setPaymentMethod(defaultMethod.id || defaultMethod.code || 'credit_debit_card');
          }
        }

        // Process onramp provider icons
        if (onrampsResult.status === 'fulfilled' && onrampsResult.value) {
          const onrampData = Array.isArray(onrampsResult.value) ? onrampsResult.value : [];
          const icons = {};
          onrampData.forEach(onramp => {
            icons[onramp.id] = onramp.icon || onramp.icons?.svg;
          });
          setProviderIcons(icons);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
        // Fall back to hardcoded data
        setFiatCurrencies(fallbackFiats);
        setCryptoCurrencies(fallbackCryptos);
        setNetworks(fallbackNetworks);
        setPaymentMethods(fallbackMethods);
        setPaymentMethod('card');
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // Re-fetch payment methods and tokens when fiat currency changes
  // Tokens are filtered by eligible providers for the selected fiat
  useEffect(() => {
    if (!fiatCurrency) return;
    const fetchFilteredData = async () => {
      paymentMethodManuallySelected.current = false;
      try {
        // Fetch payment methods and tokens filtered by fiat in parallel
        const [methodData, tokenData] = await Promise.allSettled([
          supportedService.getPaymentMethods(fiatCurrency),
          supportedService.getTokens(undefined, fiatCurrency),
        ]);

        // Update payment methods
        if (methodData.status === 'fulfilled' && Array.isArray(methodData.value) && methodData.value.length > 0) {
          const methods = methodData.value.map((m, idx) => ({
            id: m.paymentTypeId || m.id,
            code: m.paymentTypeId || m.id,
            name: m.name,
            icon: m.icon,
            recommended: idx === 0,
          }));
          setPaymentMethods(methods);
          // Always auto-select first fiat-specific method on fiat change
          setPaymentMethod(methods[0]?.id);
        }

        // Update tokens/cryptos if filtered data is available
        if (tokenData.status === 'fulfilled' && Array.isArray(tokenData.value) && tokenData.value.length > 0) {
          const tokens = tokenData.value;
          setCryptoCurrencies(tokens);
          // Auto-select first crypto if current selection is no longer available
          const currentCryptoAvailable = tokens.some(
            (t) => (t.symbol || t.code) === cryptoCurrency
          );
          if (!currentCryptoAvailable && tokens.length > 0) {
            setCryptoCurrency(tokens[0].symbol || tokens[0].code);
            if (tokens[0].network) {
              setNetwork(tokens[0].network);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to filter data by fiat:', err);
      }
    };
    fetchFilteredData();
  }, [fiatCurrency]); // intentionally excludes paymentMethod/cryptoCurrency to avoid re-trigger loops

  // Ref to track current selected provider without causing re-renders
  const selectedProviderRef = useRef(null);
  selectedProviderRef.current = selectedProvider;

  // Fetch quotes from all providers using single API call
  const fetchQuotes = useCallback(async (signal) => {
    const amount = parseFloat(fiatAmount);
    if (!fiatAmount || amount <= 0 || !fiatCurrency || !cryptoCurrency) {
      setProviderQuotes([]);
      setSelectedProvider(null);
      return;
    }

    // Validate amount against currency-specific limits
    const limits = getCurrencyLimits(fiatCurrency);
    if (amount < limits.min || amount > limits.max) {
      return;
    }

    setQuotesLoading(true);
    setQuotesError(null);
    setProviderErrors({});

    console.log('Fetching quotes with:', { fiatCurrency, cryptoCurrency, network, paymentMethod, fiatAmount: amount });

    try {
      // Single API call — if config.provider is set, only query that one provider (ramp param)
      const response = await onrampService.compareQuotes({
        fiatCurrency,
        cryptoCurrency,
        isBuyOrSell: 'BUY',
        network,
        paymentMethod: paymentMethodManuallySelected.current ? paymentMethod : undefined,
        fiatAmount: amount,
        ramp: config.provider || undefined,
      });

      // Check if aborted
      if (signal?.aborted) return;

      console.log('Compare quotes response:', response);

      // Extract quotes and metadata from response
      const { quotes = [], meta = {} } = response || {};
      const { providersFailed = [] } = meta;

      // Track failed providers
      if (providersFailed.length > 0) {
        const errors = {};
        providersFailed.forEach(provider => {
          errors[provider] = 'Quote unavailable';
        });
        setProviderErrors(errors);
      }

      // Filter by allowed providers if configured, then filter invalid quotes
      let validQuotes = quotes.filter(q => q && q.cryptoAmount > 0);
      if (config.allowedProviders?.length > 0) {
        validQuotes = validQuotes.filter(q => config.allowedProviders.includes(q.provider));
      }

      // Sort by highest payout (best price first), then mark best
      validQuotes.sort((a, b) => (b.cryptoAmount || 0) - (a.cryptoAmount || 0));
      const bestPayout = validQuotes[0]?.cryptoAmount || 0;
      const quotesWithBestPrice = validQuotes.map((quote) => ({
        ...quote,
        isBestPrice: bestPayout > 0 && quote.cryptoAmount === bestPayout
      }));

      setProviderQuotes(quotesWithBestPrice);
      setLastFetchTime(Date.now());
      setIsStale(false);

      // Auto-select provider (prefer config param, then current selection, then best price)
      if (quotesWithBestPrice.length > 0) {
        const configProvider = config.provider &&
          quotesWithBestPrice.find(q => q.provider === config.provider);
        const currentProvider = selectedProviderRef.current?.provider;
        const currentStillValid = currentProvider &&
          quotesWithBestPrice.find(q => q.provider === currentProvider);

        const newSelection = configProvider
          ? configProvider
          : currentStillValid
            ? quotesWithBestPrice.find(q => q.provider === currentProvider)
            : quotesWithBestPrice[0];

        setSelectedProvider(newSelection);

        // Announce for screen readers
        setAnnouncement(`Found ${quotesWithBestPrice.length} quotes. Best: ${quotesWithBestPrice[0]?.cryptoAmount?.toFixed(6)} ${cryptoCurrency}`);
      } else {
        setSelectedProvider(null);
        setQuotesError(providersFailed.length > 0
          ? `Failed: ${providersFailed.join(', ')}`
          : 'No quotes available.');
      }
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Error fetching quotes:', error);
      setQuotesError('Failed to fetch quotes. Please try again.');
      setProviderQuotes([]);
      setSelectedProvider(null);
    } finally {
      if (!signal?.aborted) {
        setQuotesLoading(false);
      }
    }
  }, [fiatAmount, fiatCurrency, cryptoCurrency, network, paymentMethod, config.allowedProviders, config.provider, getCurrencyLimits]);

  // Manual refresh function
  const handleRefresh = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    fetchQuotes(abortControllerRef.current.signal);
  }, [fetchQuotes]);

  // Debounce quote fetching when inputs change with AbortController
  useEffect(() => {
    if (optionsLoading) return;

    const timer = setTimeout(() => {
      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      fetchQuotes(abortControllerRef.current.signal);
    }, 500);

    return () => {
      clearTimeout(timer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fiatAmount, fiatCurrency, cryptoCurrency, network, paymentMethod, optionsLoading, fetchQuotes, fetchTrigger]);

  // Handle wallet validation
  const validateWallet = (address, required = false) => {
    if (!address || address.trim() === '') {
      if (required) {
        setWalletError('Wallet address is required');
        return false;
      }
      setWalletError(null);
      return true;
    }

    const trimmed = address.trim();
    if (trimmed.startsWith('0x')) {
      if (trimmed.length !== 42) {
        setWalletError('Address should be 42 characters');
        return false;
      }
      if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
        setWalletError('Invalid address format');
        return false;
      }
    } else if (trimmed.length < 20) {
      setWalletError('Address seems too short');
      return false;
    }

    setWalletError(null);
    return true;
  };

  // Handle provider selection with bridge notification
  const handleProviderSelect = (quote) => {
    setSelectedProvider(quote);
    if (bridge) bridge.onQuoteSelected(quote);
  };

  // Dismiss awaiting payment overlay (return to form)
  const handleDismissAwaiting = () => {
    setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
    popupWindowRef.current = null;
  };

  // Dismiss success banner
  const handleDismissCompletion = () => {
    setPaymentComplete(null);
  };

  /**
   * Handle buy button click
   *
   * When isLocked=true (payment link mode), uses the custom onCheckout handler
   * which calls the payment session checkout API with locked values.
   * When isLocked=false (normal mode), calls the standard widget URL API.
   */
  const handleBuy = async () => {
    if (!selectedProvider) return;

    // Wallet address is always required
    if (!walletAddress || walletAddress.trim() === '') {
      setWalletError('Wallet address is required');
      return;
    }

    if (!validateWallet(walletAddress, true)) {
      return;
    }

    setLoading(true);
    try {
      // Notify parent if embedded
      if (bridge) bridge.onProviderOpened(selectedProvider.provider);

      // If locked mode with custom checkout handler (payment link flow)
      // Use the custom handler which validates against the payment session
      if (isLocked && config.onCheckout) {
        await config.onCheckout({
          provider: selectedProvider.provider,
          paymentMethod: paymentMethod || undefined,
          walletAddress: walletAddress || undefined,
        });
        return;
      }

      // Wallet mode — create WalletPurchase record first, then open provider widget
      if (isWalletMode) {
        const result = await walletPurchaseService.initiatePurchase({
          walletAddress,
          provider: selectedProvider.provider,
          fiatAmount: parseFloat(fiatAmount),
          fiatCurrency,
          cryptoCurrency,
          network,
          paymentMethod: paymentMethod || undefined,
        });

        if (result?.purchaseId) {
          setActivePurchaseId(result.purchaseId);
          if (bridge) bridge.onPurchaseInitiated({
            purchaseId: result.purchaseId, provider: selectedProvider.provider,
            walletAddress, fiatAmount: parseFloat(fiatAmount), fiatCurrency, cryptoCurrency, network,
          });
        }

        if (result?.widgetUrl) {
          const providerDisplayName =
            selectedProvider.provider.charAt(0).toUpperCase() +
            selectedProvider.provider.slice(1);
          const embedAs = result.embedAs || 'tab';

          if (embedAs === 'iframe') {
            // In-page iframe — no popup, no navigation. The awaiting-payment
            // overlay renders the iframe inline (see render block below).
            setAwaitingPayment({
              active: true,
              providerName: providerDisplayName,
              widgetUrl: result.widgetUrl,
              popupBlocked: false,
              embedAs: 'iframe',
            });
          } else {
            // Open tab only after we have the URL — no blank tab flash
            const newTab = window.open(result.widgetUrl, '_blank');
            if (!newTab) {
              // Popup blocked — redirect in same window (navigates away, no polling needed)
              window.location.href = result.widgetUrl;
            } else {
              // Popup opened — track it and activate polling + BroadcastChannel listeners
              // so TRANSACTION_COMPLETE fires on success (same pattern as normal mode).
              // The overlay UI is hidden in wallet mode (guarded by !isWalletMode at render),
              // so setting awaitingPayment.active only activates the event listeners.
              popupWindowRef.current = newTab;
              setAwaitingPayment({
                active: true,
                providerName: providerDisplayName,
                widgetUrl: result.widgetUrl,
                popupBlocked: false,
                embedAs: 'tab',
              });
            }
          }
        } else {
          throw new Error('Failed to get widget URL');
        }

        if (bridge) bridge.onProviderOpened(selectedProvider.provider);
        return;
      }

      // Normal mode — create WalletPurchase record, get URL, then open tab
      const purchaseResult = await walletPurchaseService.initiatePurchase({
        walletAddress,
        provider: selectedProvider.provider,
        fiatAmount: parseFloat(fiatAmount),
        fiatCurrency,
        cryptoCurrency,
        network,
        paymentMethod: paymentMethod || undefined,
      });

      if (purchaseResult?.purchaseId) {
        setActivePurchaseId(purchaseResult.purchaseId);
        if (bridge) bridge.onPurchaseInitiated({
          purchaseId: purchaseResult.purchaseId, provider: selectedProvider.provider,
          walletAddress, fiatAmount: parseFloat(fiatAmount), fiatCurrency, cryptoCurrency, network,
        });
      }

      if (purchaseResult?.widgetUrl) {
        const providerDisplayName = selectedProvider.provider.charAt(0).toUpperCase() + selectedProvider.provider.slice(1);
        const embedAs = purchaseResult.embedAs || 'tab';

        if (embedAs === 'iframe') {
          // In-page iframe — overlay renders <iframe> inline (no popup).
          setAwaitingPayment({
            active: true,
            providerName: providerDisplayName,
            widgetUrl: purchaseResult.widgetUrl,
            popupBlocked: false,
            embedAs: 'iframe',
          });
        } else {
          // Open tab only after we have the URL — no blank tab flash
          const newTab = window.open(purchaseResult.widgetUrl, '_blank');
          const popupBlocked = !newTab || newTab.closed;

          if (newTab) {
            popupWindowRef.current = newTab;
          }

          // Show awaiting payment overlay
          setAwaitingPayment({
            active: true,
            providerName: providerDisplayName,
            widgetUrl: purchaseResult.widgetUrl,
            popupBlocked,
            embedAs: 'tab',
          });
        }
      } else {
        throw new Error('Failed to get widget URL');
      }
    } catch (error) {
      console.error('Error opening widget:', error);
      if (bridge) bridge.onError({ type: 'WIDGET_OPEN_ERROR', message: error.message });
      setWalletError(error.message || 'Failed to open widget. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Listen for widget events
  useEffect(() => {
    const handleWidgetEvent = (event) => {
      if (event.data?.event_id === 'TRANSAK_ORDER_CREATED' && event.data.data?.order_id) {
        // Notify parent if embedded
        if (bridge) bridge.onTransactionComplete(event.data.data);
        // Redirect to successUrl if configured (for exchange/wallet iframe integration)
        if (config.successUrl) {
          const url = new URL(config.successUrl);
          url.searchParams.set('orderId', event.data.data.order_id);
          if (config.orderId) url.searchParams.set('externalOrderId', config.orderId);
          window.location.href = url.toString();
        }
      }
    };
    window.addEventListener('message', handleWidgetEvent);
    return () => window.removeEventListener('message', handleWidgetEvent);
  }, [bridge, config.successUrl, config.orderId]);

  // Poll purchase status when activePurchaseId is set AND payment overlay is showing
  useEffect(() => {
    if (!activePurchaseId || !awaitingPayment.active) return;

    const TERMINAL_STATUSES = ['COMPLETED', 'FAILED', 'CANCELLED', 'EXPIRED', 'REFUNDED'];
    const POLL_INTERVAL = 10000; // 10 seconds
    const MAX_ERRORS = 3; // Stop after 3 consecutive errors
    let errorCount = 0;
    let intervalId = null;

    const poll = async () => {
      try {
        const data = await walletPurchaseService.getPurchaseStatus(activePurchaseId);
        errorCount = 0; // Reset on success
        setPurchaseStatus(data);
        if (TERMINAL_STATUSES.includes(data?.status)) {
          if (intervalId) clearInterval(intervalId);

          // Dismiss overlay and stop polling completely
          setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
          setActivePurchaseId(null);

          if (data.status === 'COMPLETED') {
            if (bridge) bridge.onTransactionComplete({ purchaseId: activePurchaseId, txHash: data.txHash });
            if (config.successUrl) {
              const url = new URL(config.successUrl);
              url.searchParams.set('purchaseId', activePurchaseId);
              if (data.txHash) url.searchParams.set('txHash', data.txHash);
              window.location.href = url.toString();
            }
          } else if (data.status === 'FAILED' && config.failureUrl) {
            window.location.href = config.failureUrl;
          }
        }
      } catch (err) {
        errorCount++;
        console.error(`Error polling purchase status (${errorCount}/${MAX_ERRORS}):`, err);
        if (errorCount >= MAX_ERRORS && intervalId) {
          clearInterval(intervalId);
          // Dismiss overlay and stop polling completely
          setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
          setActivePurchaseId(null);
          console.warn('Stopped polling: too many consecutive errors');
        }
      }
    };

    // Delay first poll by 5s to let the provider widget load
    const initialTimeout = setTimeout(() => {
      poll();
      intervalId = setInterval(poll, POLL_INTERVAL);
    }, 5000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [activePurchaseId, awaitingPayment.active, bridge, config.successUrl, config.failureUrl]);

  // Normal mode: Listen for payment completion from success page (BroadcastChannel + localStorage)
  useEffect(() => {
    if (!awaitingPayment.active) return;

    const handlePaymentComplete = (data) => {
      const upper = String(data.status || '').toUpperCase();
      const isTerminal = ['COMPLETED', 'CANCELLED', 'FAILED'].includes(upper);
      if (!isTerminal) return;

      // Cross-tab safety: the kuberpay-payment channel is shared by every
      // provider redirect on this origin. Both the widget flow (purchase.id)
      // and the payment-gateway flow (sessionId) end up broadcasting through
      // it. We only act if the broadcasted orderId matches THIS tab's active
      // wallet purchase — otherwise a payment completed in another tab/flow
      // would prematurely close our awaiting overlay.
      //
      // Skipped only when both sides are present (legacy success pages that
      // forget to pass orderId still flow through, since we have no way to
      // distinguish them from a stale broadcast).
      if (activePurchaseId && data.orderId && data.orderId !== activePurchaseId) {
        console.log('BuyCrypto: ignoring broadcast for different orderId', {
          received: data.orderId,
          expected: activePurchaseId,
        });
        return;
      }

      console.log(`BuyCrypto: Payment ${upper} detected via BroadcastChannel/localStorage:`, data);

      if (upper === 'COMPLETED') {
        setPaymentComplete({
          status: 'COMPLETED',
          orderId: data.orderId,
          txHash: data.txHash,
          timestamp: data.timestamp,
        });
      }

      // Clear awaiting overlay + stop polling for every terminal state, so the
      // user is never stuck staring at "waiting for payment" after they cancel
      // or fail at the provider.
      setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
      setActivePurchaseId(null);
      popupWindowRef.current = null;

      // Notify embedding parent (exchange/wallet iframe) via bridge.
      if (bridge) {
        if (upper === 'COMPLETED') {
          bridge.onTransactionComplete({
            orderId: data.orderId,
            txHash: data.txHash,
            status: 'COMPLETED',
          });
        } else if (bridge.onTransactionFailed) {
          bridge.onTransactionFailed({
            orderId: data.orderId,
            status: upper, // CANCELLED | FAILED
          });
        }
      }

      // Configured successUrl redirect ONLY on success — we don't want to
      // send the user away from the form on cancel/fail; they may want to retry.
      if (upper === 'COMPLETED' && config.successUrl) {
        setTimeout(() => {
          const url = new URL(config.successUrl);
          if (data.orderId) url.searchParams.set('orderId', data.orderId);
          if (data.txHash) url.searchParams.set('txHash', data.txHash);
          if (config.orderId) url.searchParams.set('externalOrderId', config.orderId);
          window.location.href = url.toString();
        }, 2000);
      }
    };

    // Method 1: BroadcastChannel (modern browsers)
    let channel = null;
    try {
      channel = new BroadcastChannel('kuberpay-payment');
      channel.onmessage = (event) => {
        if (event.data?.type === 'PAYMENT_STATUS') {
          handlePaymentComplete(event.data);
        }
      };
    } catch (err) {
      console.warn('BroadcastChannel not supported:', err);
    }

    // Method 2: localStorage (fallback for Safari)
    const handleStorageChange = (e) => {
      if (e.key === 'kuberpay-payment-status' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          handlePaymentComplete(data);
        } catch (err) {
          console.warn('Failed to parse localStorage event:', err);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Check localStorage on mount (in case success page already set it)
    try {
      const storedStatus = localStorage.getItem('kuberpay-payment-status');
      if (storedStatus) {
        const data = JSON.parse(storedStatus);
        if (data.timestamp && Date.now() - data.timestamp < 30000) {
          handlePaymentComplete(data);
        }
        localStorage.removeItem('kuberpay-payment-status');
      }
    } catch (err) {
      console.warn('Failed to check localStorage:', err);
    }

    return () => {
      if (channel) channel.close();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [awaitingPayment.active, bridge, config.successUrl, config.orderId, activePurchaseId]);

  // Normal mode: Monitor popup window — dismiss overlay if popup closed without completion
  useEffect(() => {
    if (!awaitingPayment.active || !popupWindowRef.current) return;

    const checkPopup = setInterval(() => {
      try {
        if (popupWindowRef.current && popupWindowRef.current.closed) {
          clearInterval(checkPopup);
          // Only dismiss if payment hasn't completed yet
          if (!paymentComplete) {
            setAwaitingPayment({ active: false, providerName: null, widgetUrl: null, popupBlocked: false, embedAs: null });
            setActivePurchaseId(null);
            popupWindowRef.current = null;
          }
        }
      } catch (e) {
        // Cross-origin errors are expected — ignore
      }
    }, 1000);

    return () => clearInterval(checkPopup);
  }, [awaitingPayment.active, paymentComplete]);

  // Get code helper
  const getCode = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.code || item.symbol || item.id || item.name || '';
  };

  const getName = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.displayName || item.name || item.code || '';
  };

  // Get best quote for display
  const bestQuote = providerQuotes.find(q => q.isBestPrice) || providerQuotes[0];
  const displayCryptoAmount = selectedProvider?.cryptoAmount?.toFixed(8) || bestQuote?.cryptoAmount?.toFixed(8) || '0.00000000';

  // Exchange rate from selected provider (calculate fiat per crypto)
  const fiatPerCrypto = selectedProvider?.cryptoAmount > 0
    ? selectedProvider.fiatAmount / selectedProvider.cryptoAmount
    : 0;
  const exchangeRate = fiatPerCrypto > 0
    ? `1 ${cryptoCurrency} ≈ ${fiatPerCrypto.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${fiatCurrency}`
    : '';

  // Get filtered payment methods (provider-aware: use selected provider's available methods)
  const filteredPaymentMethods = useMemo(() => {
    const providerMethods = selectedProvider?.availablePaymentMethods;

    if (providerMethods && providerMethods.length > 0) {
      return providerMethods.map((pm, idx) => ({
        id: pm.paymentTypeId,
        code: pm.paymentTypeId,
        name: pm.name,
        icon: pm.icon || null,
        recommended: idx === 0,
      }));
    }

    // Fallback: global payment methods filtered by currency
    return paymentMethods.filter(method => {
      if (method.currencies && method.currencies.length > 0) {
        return method.currencies.includes(fiatCurrency);
      }
      return true;
    });
  }, [selectedProvider, paymentMethods, fiatCurrency]);

  // Auto-select valid payment method when provider changes
  useEffect(() => {
    if (!selectedProvider?.availablePaymentMethods?.length) return;

    const providerMethodIds = selectedProvider.availablePaymentMethods.map(
      m => m.paymentTypeId
    );

    if (paymentMethod && !providerMethodIds.includes(paymentMethod)) {
      setPaymentMethod(providerMethodIds[0]);
    } else if (!paymentMethod && providerMethodIds.length > 0) {
      setPaymentMethod(providerMethodIds[0]);
    }
  }, [selectedProvider]); // intentionally excludes paymentMethod to avoid re-trigger loops

  const selectedPaymentMethod = filteredPaymentMethods.find(
    m => (m.id || m.code) === paymentMethod
  ) || filteredPaymentMethods[0];

  // Keyboard handler for provider cards
  const handleProviderKeyDown = (e, quote) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleProviderSelect(quote);
    }
  };

  // Dark theme class
  const themeClass = config.theme === 'dark' ? 'dark-theme' : '';

  // Loading state
  if (optionsLoading) {
    return (
      <div className={`widget-wrapper ${isEmbedded ? 'embedded' : ''} ${themeClass}`}>
        <div className={`widget-container ${isEmbedded ? 'embedded' : ''}`}>
          <div className="widget-body" style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div className="loading-skeleton" style={{ width: 60, height: 60, borderRadius: '50%', margin: '0 auto 16px' }}></div>
            <div className="loading-skeleton" style={{ width: 150, height: 20, margin: '0 auto' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Screen reader live region */}
      <div aria-live="polite" aria-atomic="true" className="visually-hidden">
        {announcement}
      </div>

      <div className={`widget-wrapper ${isEmbedded ? 'embedded' : ''} ${themeClass}`}>
        <div className={`widget-container ${isEmbedded ? 'embedded' : ''}`}>
          {/* Header */}
          {!config.hideHeader && (
            <div className="widget-header">
              <div className="widget-logo">
                <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
                  <circle cx="12" cy="20" r="8" stroke="currentColor" strokeWidth="3" fill="none"/>
                  <circle cx="28" cy="20" r="8" stroke="currentColor" strokeWidth="3" fill="none"/>
                </svg>
              </div>

              <div className="widget-title">Buy Crypto</div>

              <button
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={quotesLoading}
                aria-label="Refresh quotes"
                title="Refresh quotes"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                </svg>
              </button>
            </div>
          )}

          {/* Body */}
          <div className="widget-body">
            {/* Staleness warning */}
            {isStale && !quotesLoading && (
              <div className="stale-warning" role="alert">
                <span>Quotes may be outdated.</span>
                <button onClick={handleRefresh} className="refresh-link">Refresh</button>
              </div>
            )}

            {/* You Spend Section */}
            <div className="input-section">
              <label htmlFor="fiat-amount" className="input-label">
                You spend
                {/* Show locked badge when amount is locked */}
                {isAmountLocked && <span className="locked-badge">Locked</span>}
              </label>
              <div className="input-row">
                <input
                  id="fiat-amount"
                  type="number"
                  className={`amount-input ${amountError ? 'error' : ''} ${isAmountLocked ? 'locked' : ''}`}
                  value={fiatAmount}
                  onChange={(e) => {
                    if (isAmountLocked) return;
                    setFiatAmount(e.target.value);
                    validateAmount(e.target.value);
                    setFetchTrigger(prev => prev + 1);
                  }}
                  placeholder="0"
                  min="1"
                  disabled={isAmountLocked}
                  readOnly={isAmountLocked}
                  aria-describedby={amountError ? 'amount-error' : undefined}
                  aria-invalid={!!amountError}
                />

                <div className="dropdown-container" ref={fiatDropdownRef}>
                  <button
                    className={`currency-selector ${isFiatLocked ? 'locked' : ''}`}
                    onClick={() => {
                      if (isFiatLocked) return;
                      setShowFiatDropdown(!showFiatDropdown);
                    }}
                    disabled={isFiatLocked}
                    aria-label={`Select fiat currency, currently ${fiatCurrency}`}
                    aria-expanded={showFiatDropdown}
                    aria-haspopup="listbox"
                  >
                    <span className="currency-flag" aria-hidden="true">
                      {CURRENCY_FLAGS[fiatCurrency] || '💵'}
                    </span>
                    <span className="currency-code">{fiatCurrency}</span>
                    {!isFiatLocked && <span className="currency-dropdown-icon" aria-hidden="true">▼</span>}
                  </button>

                  {showFiatDropdown && !isFiatLocked && (
                    <div className="currency-dropdown-menu" role="listbox" aria-label="Fiat currencies">
                      {fiatCurrencies.map((currency) => {
                        const code = getCode(currency);
                        return (
                          <button
                            key={code}
                            className={`currency-option ${code === fiatCurrency ? 'selected' : ''}`}
                            onClick={() => {
                              setFiatCurrency(code);
                              setShowFiatDropdown(false);
                            }}
                            role="option"
                            aria-selected={code === fiatCurrency}
                          >
                            <span className="currency-flag" aria-hidden="true">{CURRENCY_FLAGS[code] || '💵'}</span>
                            <span className="currency-code">{code}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {amountError && (
                <div id="amount-error" className="amount-error" role="alert">{amountError}</div>
              )}
            </div>

            {/* You Get Section */}
            <div className="input-section">
              <span className="input-label" id="crypto-output-label">
                You get
                {isCryptoLocked && <span className="locked-badge">Locked</span>}
              </span>
              <div className="input-row">
                <span
                  className="amount-display"
                  aria-labelledby="crypto-output-label"
                  aria-live="polite"
                >
                  {quotesLoading ? (
                    <span className="loading-dots" aria-label="Loading">...</span>
                  ) : displayCryptoAmount}
                </span>

                <div className="dropdown-container" ref={cryptoDropdownRef}>
                  <button
                    className={`currency-selector crypto-selector-expanded ${isCryptoLocked ? 'locked' : ''}`}
                    onClick={() => {
                      if (isCryptoLocked) return;
                      setShowCryptoDropdown(!showCryptoDropdown);
                      setCryptoSearch('');
                    }}
                    disabled={isCryptoLocked}
                    aria-label={`Select cryptocurrency, currently ${cryptoCurrency} on ${network}`}
                    aria-expanded={showCryptoDropdown}
                    aria-haspopup="listbox"
                  >
                    {getCryptoIcon(cryptoCurrency, network) ? (
                      <img
                        className="crypto-icon"
                        src={getCryptoIcon(cryptoCurrency, network)}
                        alt=""
                        aria-hidden="true"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                        }}
                      />
                    ) : null}
                    <span
                      className="crypto-icon"
                      style={{
                        background: CRYPTO_COLORS[cryptoCurrency] || '#627eea',
                        display: getCryptoIcon(cryptoCurrency, network) ? 'none' : 'flex',
                      }}
                      aria-hidden="true"
                    >
                      {cryptoCurrency.charAt(0)}
                    </span>
                    <div className="crypto-selector-info">
                      <span className="currency-code">{cryptoCurrency}</span>
                      <span
                        className="network-badge-small"
                        style={{ background: NETWORK_COLORS[network] || '#627eea' }}
                      >
                        {network.charAt(0).toUpperCase() + network.slice(1)}
                      </span>
                    </div>
                    {!isCryptoLocked && <span className="currency-dropdown-icon" aria-hidden="true">▼</span>}
                  </button>

                  {showCryptoDropdown && !isCryptoLocked && (
                    <div className="crypto-dropdown-menu" role="listbox" aria-label="Cryptocurrencies">

                      {/* ── STEP 1: Select Token ── */}
                      {cryptoDropdownStep === 'tokens' && (
                        <>
                          <div className="crypto-dropdown-header">
                            <button
                              className="crypto-dropdown-back"
                              onClick={() => { setShowCryptoDropdown(false); setCryptoSearch(''); }}
                              aria-label="Close"
                            >
                              ←
                            </button>
                            <span className="crypto-dropdown-title">Select Token</span>
                          </div>
                          <div className="crypto-search-container">
                            <input
                              type="text"
                              className="crypto-search-input"
                              placeholder="Search token..."
                              value={cryptoSearch}
                              onChange={(e) => setCryptoSearch(e.target.value)}
                              autoFocus
                            />
                          </div>
                          <div className="crypto-options-list">
                            {tokenGroups.length === 0 && (
                              <div className="crypto-empty-state" style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
                                No tokens available for {fiatCurrency}
                              </div>
                            )}
                            {tokenGroups
                              .filter((group) => {
                                if (!cryptoSearch) return true;
                                const s = cryptoSearch.toLowerCase();
                                return (
                                  group.symbol.toLowerCase().includes(s) ||
                                  group.name.toLowerCase().includes(s) ||
                                  group.networks.some(n => (n.networkName || '').toLowerCase().includes(s))
                                );
                              })
                              .map((group) => {
                                const isSelected = group.symbol === cryptoCurrency;
                                const hasMultipleNetworks = group.networks.length > 1;
                                return (
                                  <button
                                    key={group.symbol}
                                    className={`crypto-option ${isSelected ? 'selected' : ''}`}
                                    onClick={() => {
                                      if (hasMultipleNetworks) {
                                        // Multi-network token → go to step 2
                                        setPendingTokenGroup(group);
                                        setCryptoDropdownStep('network');
                                        setCryptoSearch('');
                                      } else {
                                        // Single network → select immediately
                                        const net = group.networks[0];
                                        setCryptoCurrency(group.symbol);
                                        if (net.network) setNetwork(net.network);
                                        setShowCryptoDropdown(false);
                                        setCryptoDropdownStep('tokens');
                                        setCryptoSearch('');
                                      }
                                    }}
                                    role="option"
                                    aria-selected={isSelected}
                                  >
                                    <div className="crypto-option-left">
                                      {group.icon ? (
                                        <img
                                          className="crypto-icon-large"
                                          src={group.icon}
                                          alt=""
                                          aria-hidden="true"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                          }}
                                        />
                                      ) : null}
                                      <span
                                        className="crypto-icon-large"
                                        style={{
                                          background: CRYPTO_COLORS[group.symbol] || '#627eea',
                                          display: group.icon ? 'none' : 'flex',
                                        }}
                                        aria-hidden="true"
                                      >
                                        {group.symbol.charAt(0)}
                                      </span>
                                      <div className="crypto-option-info">
                                        <span className="crypto-option-name">{group.name}</span>
                                        <span className="crypto-option-code">{group.symbol}</span>
                                      </div>
                                    </div>
                                    {hasMultipleNetworks ? (
                                      <span className="network-count-badge">
                                        {group.networks.length} networks <span aria-hidden="true">›</span>
                                      </span>
                                    ) : (
                                      <span
                                        className="network-badge-single"
                                        style={{ background: NETWORK_COLORS[group.networks[0]?.network] || '#627eea' }}
                                      >
                                        {group.networks[0]?.networkName || group.networks[0]?.network}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                          </div>
                        </>
                      )}

                      {/* ── STEP 2: Select Network for chosen token ── */}
                      {cryptoDropdownStep === 'network' && pendingTokenGroup && (
                        <>
                          <div className="crypto-dropdown-header">
                            <button
                              className="crypto-dropdown-back"
                              onClick={() => { setCryptoDropdownStep('tokens'); setPendingTokenGroup(null); }}
                              aria-label="Back to tokens"
                            >
                              ←
                            </button>
                            <span className="crypto-dropdown-title">
                              Select Network
                            </span>
                          </div>
                          <div className="network-step-token-info">
                            {pendingTokenGroup.icon ? (
                              <img className="network-step-token-icon" src={pendingTokenGroup.icon} alt="" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                            ) : null}
                            <span
                              className="network-step-token-icon"
                              style={{ background: CRYPTO_COLORS[pendingTokenGroup.symbol] || '#627eea', display: pendingTokenGroup.icon ? 'none' : 'flex' }}
                            >
                              {pendingTokenGroup.symbol.charAt(0)}
                            </span>
                            <div>
                              <div className="network-step-token-name">{pendingTokenGroup.name}</div>
                              <div className="network-step-token-subtitle">{pendingTokenGroup.symbol} is available on {pendingTokenGroup.networks.length} networks</div>
                            </div>
                          </div>
                          <div className="network-step-list">
                            {pendingTokenGroup.networks
                              .sort((a, b) => {
                                // Sort: popular networks first
                                const POPULAR_NETS = ['ethereum', 'polygon', 'bsc', 'tron', 'solana', 'arbitrum', 'base', 'optimism'];
                                const ai = POPULAR_NETS.indexOf((a.network || '').toLowerCase());
                                const bi = POPULAR_NETS.indexOf((b.network || '').toLowerCase());
                                if (ai !== -1 && bi !== -1) return ai - bi;
                                if (ai !== -1) return -1;
                                if (bi !== -1) return 1;
                                return (a.networkName || '').localeCompare(b.networkName || '');
                              })
                              .map((net) => {
                                const isCurrentNetwork = net.network === network && pendingTokenGroup.symbol === cryptoCurrency;
                                return (
                                  <button
                                    key={net.network}
                                    className={`network-step-item ${isCurrentNetwork ? 'selected' : ''}`}
                                    onClick={() => {
                                      setCryptoCurrency(pendingTokenGroup.symbol);
                                      setNetwork(net.network);
                                      setShowCryptoDropdown(false);
                                      setCryptoDropdownStep('tokens');
                                      setPendingTokenGroup(null);
                                      setCryptoSearch('');
                                    }}
                                  >
                                    <span
                                      className="network-step-dot"
                                      style={{ background: NETWORK_COLORS[net.network] || '#627eea' }}
                                    />
                                    <div className="network-step-info">
                                      <span className="network-step-name">{net.networkName || net.network}</span>
                                    </div>
                                    {isCurrentNetwork && <span className="network-step-check">✓</span>}
                                  </button>
                                );
                              })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Network indicator — clickable to change network */}
            {!isCryptoLocked && (
              <div className="network-change-row">
                <span className="network-change-label">Network</span>
                <button
                  className="network-change-btn"
                  onClick={() => {
                    // Find current token's group and go directly to network step
                    const group = tokenGroups.find(g => g.symbol === cryptoCurrency);
                    if (group && group.networks.length > 1) {
                      setPendingTokenGroup(group);
                      setCryptoDropdownStep('network');
                      setShowCryptoDropdown(true);
                    } else {
                      // Single network token — open token selector instead
                      setCryptoDropdownStep('tokens');
                      setShowCryptoDropdown(true);
                    }
                  }}
                >
                  <span
                    className="network-change-dot"
                    style={{ background: NETWORK_COLORS[network] || '#627eea' }}
                  />
                  <span className="network-change-name">
                    {(() => {
                      const group = tokenGroups.find(g => g.symbol === cryptoCurrency);
                      const net = group?.networks.find(n => n.network === network);
                      return net?.networkName || network.charAt(0).toUpperCase() + network.slice(1);
                    })()}
                  </span>
                  <span className="network-change-arrow" aria-hidden="true">›</span>
                </button>
              </div>
            )}

            {/* Exchange Rate */}
            {exchangeRate && (
              <div className="exchange-rate-display" aria-label={`Exchange rate: ${exchangeRate}`}>
                {exchangeRate}
              </div>
            )}

            {/* Best Price Section - Single provider display with "See All" */}
            {(quotesLoading || selectedProvider || quotesError) && (
              <div className="best-price-section" role="region" aria-label="Best price">
                {quotesLoading ? (
                  <div className="best-price-loading" aria-busy="true">
                    <div className="provider-skeleton"></div>
                  </div>
                ) : quotesError ? (
                  <div className="providers-error" role="alert">{quotesError}</div>
                ) : selectedProvider ? (
                  <div className="best-price-card">
                    <div className="best-price-header">
                      <span className="best-price-label">Best Price</span>
                      <div className="best-price-header-right">
                        <div className="best-price-provider">
                          <div className="provider-card-logo small">
                            {(providerIcons[selectedProvider.provider] || PROVIDER_LOGOS[selectedProvider.provider]) ? (
                              <img
                                src={providerIcons[selectedProvider.provider] || PROVIDER_LOGOS[selectedProvider.provider]}
                                alt=""
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                }}
                              />
                            ) : null}
                            <span style={{ display: (providerIcons[selectedProvider.provider] || PROVIDER_LOGOS[selectedProvider.provider]) ? 'none' : 'flex' }}>
                              {selectedProvider.provider.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="best-price-provider-name">
                            via {selectedProvider.provider.charAt(0).toUpperCase() + selectedProvider.provider.slice(1)}
                          </span>
                        </div>
                        {providerQuotes.length > 1 && (
                          <button
                            className="see-all-btn"
                            onClick={() => setShowProvidersModal(true)}
                            aria-label={`See all ${providerQuotes.length} provider quotes`}
                          >
                            See All ({providerQuotes.length})
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="best-price-details">
                      <div className="best-price-fee">
                        <button
                          className="fee-toggle"
                          onClick={() => setShowFeeBreakdown(showFeeBreakdown === selectedProvider.provider ? null : selectedProvider.provider)}
                          aria-expanded={showFeeBreakdown === selectedProvider.provider}
                          aria-label="Show fee breakdown"
                        >
                          {selectedProvider.fees?.total > 0 ? `Fee: ${selectedProvider.fees.total.toFixed(2)} ${fiatCurrency}` : 'View fees'}
                          <span aria-hidden="true">{showFeeBreakdown === selectedProvider.provider ? '▲' : '▼'}</span>
                        </button>
                      </div>
                    </div>
                    {showFeeBreakdown === selectedProvider.provider && (
                      <div className="fee-breakdown" aria-label="Fee breakdown">
                        <div className="fee-row">
                          <span>Network fee</span>
                          <span>{selectedProvider.fees?.network?.toFixed(2) || '0.00'} {fiatCurrency}</span>
                        </div>
                        <div className="fee-row">
                          <span>Provider fee</span>
                          <span>{(selectedProvider.fees?.service ?? selectedProvider.fees?.provider)?.toFixed(2) || '0.00'} {fiatCurrency}</span>
                        </div>
                        <div className="fee-row total">
                          <span>Total fees</span>
                          <span>{selectedProvider.fees?.total?.toFixed(2) || '0.00'} {fiatCurrency}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Payment Method */}
            <div className="payment-section">
              <label className="payment-label" id="payment-method-label">Pay using</label>

              <div className="dropdown-container" ref={paymentDropdownRef}>
                <button
                  className="payment-selector"
                  onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
                  aria-labelledby="payment-method-label"
                  aria-expanded={showPaymentDropdown}
                  aria-haspopup="listbox"
                >
                  <div className="payment-left">
                    <div className="payment-icon" aria-hidden="true">
                      {selectedPaymentMethod?.name?.includes('UPI') ? 'UPI' : '💳'}
                    </div>
                    <div className="payment-info">
                      <span className="payment-name">
                        {selectedPaymentMethod?.name || 'Select payment method'}
                      </span>
                      {selectedPaymentMethod?.recommended && (
                        <span className="payment-recommended">Recommended</span>
                      )}
                    </div>
                  </div>
                  <span className="payment-dropdown-icon" aria-hidden="true">▼</span>
                </button>

                {showPaymentDropdown && (
                  <div className="payment-dropdown-menu" role="listbox" aria-label="Payment methods">
                    {filteredPaymentMethods.map((method) => {
                      const methodId = method.id || method.code;
                      return (
                        <button
                          key={methodId}
                          className={`payment-option ${methodId === paymentMethod ? 'selected' : ''}`}
                          onClick={() => {
                            paymentMethodManuallySelected.current = true;
                            setPaymentMethod(methodId);
                            setShowPaymentDropdown(false);
                          }}
                          role="option"
                          aria-selected={methodId === paymentMethod}
                        >
                          <div className="payment-left">
                            <div className="payment-icon" aria-hidden="true">
                              {method.name?.includes('UPI') ? 'UPI' : '💳'}
                            </div>
                            <div className="payment-info">
                              <span className="payment-name">{method.name || methodId}</span>
                              {method.recommended && (
                                <span className="payment-recommended">Recommended</span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Wallet Address */}
            <div className="wallet-section">
              <label htmlFor="wallet-address" className="input-label">
                Wallet Address
                {isWalletLocked && <span className="locked-badge">Locked</span>}
              </label>
              <div className="wallet-input-container">
                <input
                  id="wallet-address"
                  type="text"
                  className={`wallet-input ${walletError ? 'error' : ''} ${isWalletLocked ? 'locked' : ''}`}
                  value={walletAddress}
                  onChange={(e) => {
                    if (isWalletLocked) return;
                    setWalletAddress(e.target.value);
                    setWalletError(null);
                    validateWallet(e.target.value);
                  }}
                  placeholder={!walletAddress ? 'Connect wallet to continue' : ''}
                  disabled={isWalletLocked}
                  readOnly={isWalletLocked}
                  aria-describedby={walletError ? 'wallet-error' : undefined}
                  aria-invalid={!!walletError}
                  aria-required={true}
                />
                {walletError && <div id="wallet-error" className="wallet-error" role="alert">{walletError}</div>}
              </div>
            </div>

            {/* Wallet Purchase Status Banner */}
            {isWalletMode && purchaseStatus && (
              <div className={`purchase-status-banner status-${purchaseStatus.status?.toLowerCase()}`} role="status">
                <div className="purchase-status-icon">
                  {purchaseStatus.status === 'COMPLETED' ? '✓' :
                   purchaseStatus.status === 'FAILED' ? '✕' :
                   purchaseStatus.status === 'PROCESSING' ? '⟳' :
                   purchaseStatus.status === 'AWAITING_PAYMENT' ? '◷' : '•'}
                </div>
                <div className="purchase-status-text">
                  <span className="purchase-status-label">
                    {purchaseStatus.status === 'INITIATED' && 'Purchase Initiated'}
                    {purchaseStatus.status === 'AWAITING_PAYMENT' && 'Awaiting Payment'}
                    {purchaseStatus.status === 'PROCESSING' && 'Processing...'}
                    {purchaseStatus.status === 'COMPLETED' && 'Purchase Complete!'}
                    {purchaseStatus.status === 'FAILED' && 'Purchase Failed'}
                    {purchaseStatus.status === 'EXPIRED' && 'Purchase Expired'}
                    {purchaseStatus.status === 'CANCELLED' && 'Purchase Cancelled'}
                  </span>
                  {purchaseStatus.txHash && (
                    <span className="purchase-tx-hash">
                      Tx: {purchaseStatus.txHash.slice(0, 10)}...{purchaseStatus.txHash.slice(-6)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Payment Success Banner (normal mode) */}
            {paymentComplete?.status === 'COMPLETED' && (
              <div className="bc-success-banner" role="status">
                <div className="bc-success-banner-content">
                  <div className="bc-success-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="9,12 11,14 15,10" />
                    </svg>
                  </div>
                  <div className="bc-success-info">
                    <span className="bc-success-label">Payment Successful!</span>
                    {paymentComplete.txHash && (
                      <span className="bc-success-tx">
                        Tx: {paymentComplete.txHash.slice(0, 10)}...{paymentComplete.txHash.slice(-6)}
                      </span>
                    )}
                    {config.successUrl && <span className="bc-success-redirect">Redirecting...</span>}
                  </div>
                </div>
                <div className="bc-success-actions">
                  {config.successUrl && (
                    <button
                      className="bc-success-return"
                      onClick={() => {
                        const url = new URL(config.successUrl);
                        if (paymentComplete.orderId) url.searchParams.set('orderId', paymentComplete.orderId);
                        if (paymentComplete.txHash) url.searchParams.set('txHash', paymentComplete.txHash);
                        if (config.orderId) url.searchParams.set('externalOrderId', config.orderId);
                        window.location.href = url.toString();
                      }}
                    >
                      Return
                    </button>
                  )}
                  <button className="bc-success-dismiss" onClick={handleDismissCompletion} aria-label="Dismiss">
                    &times;
                  </button>
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <label className="disclaimer-container">
              <input
                type="checkbox"
                className="disclaimer-checkbox"
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
              />
              <span className="disclaimer-text">
                I understand that I will be purchasing cryptocurrency directly through 3rd party service providers.
                KuberCrypto is an aggregation platform; hence, will not take responsibility for any issues
                that may affect my transactions through the 3rd party service providers.
              </span>
            </label>

            {/* Buy Button */}
            <button
              className={`buy-button ${loading ? 'loading' : ''}`}
              onClick={handleBuy}
              disabled={loading || !selectedProvider || quotesLoading || !!amountError || !disclaimerAccepted || !walletAddress || !!walletError || awaitingPayment.active || paymentComplete?.status === 'COMPLETED'}
              aria-busy={loading}
            >
              {!walletAddress
                ? 'Connect Wallet to Continue'
                : walletError
                ? 'Enter a Valid Wallet Address'
                : !disclaimerAccepted
                ? 'Accept Disclaimer to Continue'
                : `Buy ${cryptoCurrency}`}
            </button>
          </div>

          {/* Footer removed */}

          {/* Provider Selection Modal — overlays within the widget card */}
          {showProvidersModal && (
            <div className="providers-modal-overlay" onClick={() => setShowProvidersModal(false)}>
              <div className="providers-modal" onClick={(e) => e.stopPropagation()}>
                <div className="providers-modal-header">
                  <h3 className="providers-modal-title">Select Provider</h3>
                  <button
                    className="providers-modal-close"
                    onClick={() => setShowProvidersModal(false)}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
                <div className="providers-modal-subtitle">
                  {providerQuotes.length} providers available for {fiatAmount} {fiatCurrency} &rarr; {cryptoCurrency}
                </div>
                <div className="providers-modal-list">
                  {providerQuotes.map((quote) => {
                    const isSelected = selectedProvider?.provider === quote.provider;
                    const providerLogoUrl = providerIcons[quote.provider] || PROVIDER_LOGOS[quote.provider];
                    return (
                      <button
                        key={quote.provider}
                        className={`providers-modal-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          handleProviderSelect(quote);
                          setShowProvidersModal(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleProviderSelect(quote);
                            setShowProvidersModal(false);
                          }
                        }}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <div className="providers-modal-card-left">
                          <div className="provider-card-logo">
                            {providerLogoUrl ? (
                              <img
                                src={providerLogoUrl}
                                alt=""
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                }}
                              />
                            ) : null}
                            <span style={{ display: providerLogoUrl ? 'none' : 'flex' }}>
                              {quote.provider.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="providers-modal-card-info">
                            <span className="providers-modal-card-name">
                              {quote.provider.charAt(0).toUpperCase() + quote.provider.slice(1)}
                              {quote.isBestPrice && <span className="best-price-badge">Best</span>}
                            </span>
                            <span className="providers-modal-card-fees">
                              Fee: {quote.fees?.total?.toFixed(2) || '0.00'} {fiatCurrency}
                            </span>
                          </div>
                        </div>
                        <div className="providers-modal-card-right">
                          <span className="providers-modal-card-amount">
                            {quote.cryptoAmount?.toFixed(6)} {cryptoCurrency}
                          </span>
                          <span className="providers-modal-card-rate">
                            {quote.cryptoAmount > 0 ? `1 ${cryptoCurrency} = ${(quote.fiatAmount / quote.cryptoAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${fiatCurrency}` : ''}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="providers-modal-check" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Awaiting Payment Overlay (normal mode only) */}
          {awaitingPayment.active && !isWalletMode && !isLocked && (
            <div className="bc-awaiting-overlay">
              {/* Close button */}
              <button
                className="bc-awaiting-close"
                onClick={handleDismissAwaiting}
                aria-label="Close and return to form"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>

              {awaitingPayment.embedAs === 'iframe' && awaitingPayment.widgetUrl ? (
                /* In-page iframe (Guardarian & future iframe providers).
                   `allow` grants the permissions required for KYC selfie +
                   3-D-Secure flows; do NOT add a `sandbox` attribute, it
                   silently strips camera/microphone access. */
                <div className="bc-awaiting-iframe-wrap">
                  <iframe
                    src={awaitingPayment.widgetUrl}
                    title={`${awaitingPayment.providerName} checkout`}
                    allow="camera; microphone; fullscreen; payment; accelerometer; autoplay; gyroscope"
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '600px',
                      border: 0,
                    }}
                  />
                </div>
              ) : (
              <div className="bc-awaiting-content">
                {/* Pulsing animation */}
                <div className="bc-awaiting-pulse-container">
                  <div className="bc-awaiting-pulse"></div>
                  <div className="bc-awaiting-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  </div>
                </div>

                <h3 className="bc-awaiting-title">Complete Your Payment</h3>

                <p className="bc-awaiting-message">
                  Please complete your payment on the <strong>{awaitingPayment.providerName}</strong> page.
                </p>

                {awaitingPayment.popupBlocked && (
                  <div className="bc-awaiting-blocked">
                    <span>Popup was blocked. Click below to open the payment page:</span>
                  </div>
                )}

                {awaitingPayment.widgetUrl && (
                  <a
                    href={awaitingPayment.widgetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bc-awaiting-link"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                      <polyline points="15,3 21,3 21,9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                    Open {awaitingPayment.providerName} Payment Page
                  </a>
                )}

                <div className="bc-awaiting-waiting">
                  <div className="bc-awaiting-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span>Waiting for payment confirmation...</span>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BuyCrypto;
